import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck } from 'lucide-react';
import { INFORMATIVA } from '@/content/informativaCondomini';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Finestra che il condomino trova entrando, finche' non ha letto e accettato la versione corrente dell'informativa privacy.
// Non si chiude da sola: solo «Accetto e continuo» (o «Esci»). Con «entra come» e' un'anteprima: l'accettazione la fa il condomino.
// Fino all'accettazione l'altro avviso («Controlla la tua posizione») aspetta: vedi window.__informativaOk.
export const InformativaPrivacyCondomino = () => {
  const { token, selectedCondominium, accessoComeCondomino, logout } = useAuth();
  const eCondomino = selectedCondominium?.role === 'RESIDENT';
  const [stato, setStato] = useState(null);       // null = non ancora letto
  const [spunta, setSpunta] = useState(false);
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState('');

  useEffect(() => {
    if (!eCondomino || !token) return;
    window.__informativaOk = false;       // si riparte da capo a ogni accesso o cambio di utente
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/informativa-condomini/stato`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error();
        const j = await res.json();
        setStato(j);
        if (j.accettata) { window.__informativaOk = true; window.dispatchEvent(new Event('informativa-ok')); }
      } catch (e) {
        // se non si riesce a leggere lo stato non si blocca l'app: la finestra ritentera' al prossimo accesso
        window.__informativaOk = true; window.dispatchEvent(new Event('informativa-ok'));
      }
    })();
  }, [eCondomino, token]);

  if (!eCondomino || !stato || stato.accettata) return null;

  const accetta = async () => {
    setInvio(true); setErrore('');
    try {
      const res = await fetch(`${API_URL}/api/informativa-condomini/accetta`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ versione: stato.versione }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'Non sono riuscito a registrare l\'accettazione');
      setStato({ ...stato, accettata: true });
      window.__informativaOk = true; window.dispatchEvent(new Event('informativa-ok'));
    } catch (e) { setErrore(e.message); }
    setInvio(false);
  };

  const chiudiAnteprima = () => { setStato({ ...stato, accettata: true }); window.__informativaOk = true; window.dispatchEvent(new Event('informativa-ok')); };

  return (
    <Dialog open onOpenChange={() => { /* non si chiude senza una scelta */ }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-700" /> I tuoi dati personali</DialogTitle>
          <DialogDescription>Prima di continuare, leggi come usiamo i tuoi dati nella piattaforma del condominio.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm leading-relaxed">
          {INFORMATIVA.riassunto.map((r) => (
            <div key={r.titolo}>
              <p className="font-semibold">{r.titolo}</p>
              <p className="text-muted-foreground">{r.testo}</p>
            </div>
          ))}
          <p>
            L'informativa completa, con i tuoi diritti e i recapiti per esercitarli, è qui:{' '}
            <Link to="/informativa-condomini" target="_blank" rel="noopener noreferrer" className="underline font-medium">leggi l'informativa completa</Link>.
            Resta sempre disponibile dal fondo di ogni pagina.
          </p>
          {accessoComeCondomino ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
              Stai guardando l'app come condomino (accesso dell'amministratore): questa è solo un'anteprima, l'accettazione la fa il condomino al suo primo accesso.
            </div>
          ) : (
            <label className="flex items-start gap-2 rounded-md border p-3">
              <input type="checkbox" className="mt-1" checked={spunta} onChange={(e) => setSpunta(e.target.checked)} />
              <span>{INFORMATIVA.testoAccettazione}</span>
            </label>
          )}
          {errore && <p className="text-red-700">{errore}</p>}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {accessoComeCondomino ? (
            <Button onClick={chiudiAnteprima}>Chiudi l'anteprima</Button>
          ) : (
            <>
              <Button variant="outline" onClick={logout} disabled={invio}>Esci</Button>
              <Button onClick={accetta} disabled={!spunta || invio}>Accetto e continuo</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InformativaPrivacyCondomino;
